import { React, useRef, useState } from '../../shared/react.js';
import { Icon } from '../../shared/icons.js';
import { ArtworkImage } from '../../shared/ui/ArtworkImage.js';
import { artworkById, queryScopeDescription } from '../../app/app.selectors.js';
import { useAppRuntime, useAppState } from '../../app/app.context.js';
import { QueryFilterMenu } from './QueryFilterMenu.js';

export function QueryCanvas() {
  const runtime = useAppRuntime();
  const state = useAppState();
  const fileRef = useRef(null as HTMLInputElement | null);
  const filterRef = useRef(null as HTMLButtonElement | null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <section className="query-canvas" aria-labelledby="query-canvas-title">
      <div className="query-canvas__heading">
        <h2 id="query-canvas-title">Query canvas</h2>
        <span title={runtime.experimentalVisual ? 'Combine text, visual references, selected details, exclusions, and catalog filters.' : 'Search the catalog with text and filters.'}><Icon name="help" size={14}/></span>
      </div>

      <p className="query-canvas__scope" aria-live="polite">{runtime.experimentalVisual ? <>{queryScopeDescription(state)} <span>Experimental references use color, light and composition; no object recognition.</span></> : `Searches ${(state.corpus?.items.length ?? 0).toLocaleString()} catalog records.`}</p>

      <div className="query-canvas__terms">
        {state.queryTerms.map((term) => {
          const artwork = artworkById(state, term.artworkId ?? null);
          const preview = term.previewUrl ?? artwork?.thumbnailUrl ?? artwork?.imageUrl;
          return (
            <article className={`query-term query-term--${term.polarity}`} key={term.id}>
              <span className="query-term__visual">
                {preview ? (
                  <ArtworkImage
                    src={preview}
                    alt=""
                    fallbackLabel="Ref"
                    width={artwork?.imageWidth}
                    height={artwork?.imageHeight}
                    dominantColor={artwork?.dominantColor}
                  />
                ) : (
                  <Icon name={term.kind === 'text' ? 'text' : term.kind === 'detail' ? 'crop' : 'image'} size={15}/>
                )}
              </span>
              <span className="query-term__copy">
                <strong title={term.label}>{term.label}</strong>
                <small>{term.kind === 'text' ? 'Text' : term.kind === 'detail' ? 'Selected detail' : term.kind === 'image' ? 'Image reference' : term.pinned ? 'Pinned artwork' : 'Artwork reference'}</small>
              </span>
              {runtime.experimentalVisual && <button
                type="button"
                className={`query-term__polarity query-term__polarity--${term.polarity}`}
                onClick={() => void runtime.setQueryTermPolarity(term.id, term.polarity === 'positive' ? 'negative' : 'positive')}
                aria-label={`Use ${term.label} as ${term.polarity === 'positive' ? 'negative' : 'positive'} evidence`}
              >
                {term.polarity === 'positive' ? 'Positive' : 'Negative'}
              </button>}
              {(term.kind !== 'text' || state.queryTerms.some((other) => other.id !== term.id && other.polarity === 'positive')) && <button type="button" className="query-term__remove" onClick={() => void runtime.removeQueryTerm(term.id)} aria-label={`Remove ${term.label}`}>
                <Icon name="close" size={13}/>
              </button>}
            </article>
          );
        })}
      </div>

      <div className="query-canvas__chips" aria-label="Active filters">
        {state.queryFilters.map((filter) => (
          <button type="button" key={filter.id} onClick={() => void runtime.removeFilter(filter.id)} title={`Remove ${filter.label}`}>
            <span>{filter.label}</span><Icon name="close" size={12}/>
          </button>
        ))}
        {state.scopedCollectionId && (
          <button type="button" onClick={() => void runtime.scopeCollection(null)} title="Search the complete collection">
            <span>{state.savedCollections.find((collection) => collection.id === state.scopedCollectionId)?.name ?? 'Saved collection'}</span><Icon name="close" size={12}/>
          </button>
        )}
      </div>

      <div className="query-canvas__actions">
        {runtime.experimentalVisual && <><input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/avif"
          onChange={(event: any) => {
            const file = event.currentTarget.files?.[0] as File | undefined;
            if (file) void runtime.searchUpload(file);
            event.currentTarget.value = '';
          }}
          aria-label="Add an image reference"
        />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={!state.corpus || state.indexPhase === 'visualizing'}>
          <Icon name="image" size={14}/><span>Add reference</span>
        </button></>}
        <div className="query-filter-anchor">
          <button ref={filterRef} type="button" aria-haspopup="dialog" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((open: boolean) => !open)}>
            <Icon name="filter" size={14}/><span>Add filter</span>
          </button>
          {filtersOpen && <QueryFilterMenu anchor={filterRef.current} onClose={() => setFiltersOpen(false)}/>}
        </div>
      </div>
    </section>
  );
}
